import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";

import toast, { Toaster } from "react-hot-toast";
import {
  Mail,
  Smartphone,
  ShieldCheck,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import AuthLayout from "../helpers/AuthLayout";
import SummaryApi from "../common";
import { useGoogleLogin } from "@react-oauth/google";

const Login = () => {
  const navigate = useNavigate();
   const location = useLocation();
  const [mode, setMode] = useState("phone"); 
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canResend, setCanResend] = useState(true);         
  const [resendCountdown, setResendCountdown] = useState(0);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phone: "",
    otp: "",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  // COUNTDOWN LOGIC
  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setInterval(() => {
        setResendCountdown((prev) => prev - 1);
      }, 1000);
    } else if (resendCountdown === 0) {
      setCanResend(true);
      clearInterval(timer);
    }
    return () => clearInterval(timer); // Cleanup on unmount
  }, [resendCountdown]);

  // ✅ FIXED TOAST LOGIC: Same ID prevents stacking, no duration uses global mobile rules
  const triggerToast = (message, type = "success", title = "") => {
    const isSuccess = type === "success";
toast.dismiss("auth-toast");
    toast.custom(
      (t) => (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className={`max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] pointer-events-auto flex ring-1 ring-black ring-opacity-5 overflow-hidden border-b-4 ${
            isSuccess ? "border-rose-500" : "border-red-500"
          } z-[999999]`}
        >
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                    isSuccess ? "bg-rose-100 text-rose-500" : "bg-red-100 text-red-500"
                  }`}>
                  {isSuccess ? <CheckCircle size={28} /> : <AlertCircle size={28} />}
                </div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-bold text-rose-950">
                  {title || (isSuccess ? "Success" : "Action Failed")}
                </p>
                <p className="mt-1 text-xs text-rose-900/50 font-black tracking-wider">
                  {message}
                </p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-rose-50">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-bold text-rose-600 hover:text-rose-500 focus:outline-none"
            >
              Close
            </button>
          </div>
        </motion.div>
      ),
           { id: "auth-toast", duration: 5000 }
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      if (value === "" || /^\d+$/.test(value))
        setFormData((prev) => ({ ...prev, phone: value.slice(0, 10) }));
    } else if (name === "otp") {
      if (value === "" || /^\d+$/.test(value))
        setFormData((prev) => ({ ...prev, otp: value.slice(0, 6) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const sendOtp = async () => {
    if (!canResend) return;
    if (formData.phone.length !== 10)
      return triggerToast("Enter a valid 10-digit number", "error");

    setLoading(true);
    try {
      const response = await fetch(SummaryApi.generateOtp.url, {
        method: SummaryApi.generateOtp.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile_number: formData.phone }),
      });
      
      const result = await response.json();
      const receivedOtp = result.mobile_otp || (result.data && result.data.mobile_otp);

      if (result.status === true || receivedOtp) {
        setOtpSent(true);
        setCanResend(false);
        setResendCountdown(60); 
        setTimeout(() => {
            triggerToast(
                `Your verification code is: ${receivedOtp}`, 
                "success",
                "OTP Sent! 📱"
            );
        }, 100);
      } else {
        triggerToast(result.message || "Failed to send OTP", "error");
      }
    } catch (error) {
      triggerToast("Server error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let payload = { mobile_number: formData.phone, mobile_otp: formData.otp };
      const response = await fetch(SummaryApi.VerifyOtp.url, {
        method: SummaryApi.VerifyOtp.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status === true || data.user_id) {
        const userId = data.user_id || (data.user && data.user.id);
        const userData = {
          ...data, 
        user_id: data.user_id || (data.user && data.user.id),
        };

     localStorage.setItem("user", JSON.stringify(userData));

        try {
          const subResponse = await fetch(SummaryApi.expiry_subscription_by_user.url, {
            method: SummaryApi.expiry_subscription_by_user.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: userId }),
          });
          const subData = await subResponse.json();
          if (subData) localStorage.setItem("subscription_info", JSON.stringify(subData));
        } catch (subError) {
          console.error("Subscription check failed:", subError);
        }

        triggerToast("Welcome back! Redirecting...", "success", "Login Successful 🎉");
         const isFromSignup = location.state?.fromSignup;
        setTimeout(() => {
          if (isFromSignup) {
            navigate("/", { replace: true }); // Go home if from signup
          } else {
            navigate(-1); // Go back if from any other page (Cart, Quiz, etc)
          }
        }, 1000);
      } else {
        triggerToast(data.message || "Invalid credentials", "error");
      }
    } catch (error) {
      triggerToast("Connection error.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const googleUser = await res.json();

        const response = await fetch(SummaryApi.googleLogin.url, {
          method: SummaryApi.googleLogin.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: googleUser.email,
            user_name: googleUser.name,
            google_id: googleUser.sub,
          }),
        });

        const data = await response.json();

        if (data.status === true) {
           localStorage.setItem("user", JSON.stringify({
          ...data,
          user_id: data.user_id,
        }));
          
          triggerToast("Welcome back!", "success", "Login Successful 🎉");
          setTimeout(() => navigate("/"), 1500);
        } else {
          triggerToast(data.message || "Google login failed", "error", "Account Issue ⚠️");
        }
      } catch (error) {
        triggerToast("Connection error.", "error", "Auth Failed");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <AuthLayout>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md bg-white/20 backdrop-blur-[25px] border border-white/40 rounded-[2rem] p-10 md:p-14 shadow-2xl"
      >
        <h2 className="text-3xl font-bold text-rose-900 text-center mb-8 tracking-widest uppercase">
          Login
        </h2>
   
        <form onSubmit={handleLogin} className="space-y-6">
          <div className="relative border-b-2 border-rose-900/20 focus-within:border-rose-500 py-2">
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="Mobile Number"
              className="w-full bg-transparent outline-none text-rose-950 placeholder:text-rose-900/40 py-1"
            />
            <Smartphone className="absolute right-0 top-2 text-rose-900/60" size={20} />
          </div>

          {otpSent && (
            <div className="relative border-b-2 border-rose-900/20 focus-within:border-rose-500 py-2">
              <input
                type="text"
                name="otp"
                required
                value={formData.otp}
                onChange={handleChange}
                placeholder="Enter OTP"
                className="w-full bg-transparent outline-none text-rose-950 placeholder:text-rose-900/40 py-1"
              />
              <ShieldCheck className="absolute right-0 top-2 text-rose-900/60" size={20} />
            </div>
          )}

          {!otpSent && (
            <motion.button
              type="button"
              whileTap={{ scale: 0.97 }}
              onClick={sendOtp}
              disabled={loading}
              className="w-full bg-white/40 text-rose-900 font-bold py-3 rounded-xl flex items-center justify-center shadow-sm"
            >
              {loading ? <Loader2 className="animate-spin" /> : "Send OTP"}
            </motion.button>
          )}

          {otpSent && (
            <div className="text-center text-sm mt-3">
              Didn't receive code?{" "}
              {canResend ? (
                <button
                  type="button"
                  onClick={sendOtp}
                  disabled={loading}
                  className="text-rose-600 font-bold hover:underline"
                >
                  Resend OTP
                </button>
              ) : (
                <span className="text-rose-500/70 font-mono">
                  Resend in {resendCountdown}s
                </span>
              )}
            </div>
          )}

          <div className="flex flex-col items-center gap-4 py-4">
            <p className="text-sm font-medium text-rose-900">Login using</p>
            <button
              type="button"
              onClick={() => handleGoogleLogin()}
              disabled={loading}
              className="h-12 w-12 rounded-full bg-white shadow-md flex items-center justify-center 
                         text-rose-600 hover:bg-rose-50 transition-all duration-300 disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : <Mail size={22} />}
            </button>
          </div>

          {otpSent && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-rose-400 to-rose-600 text-white font-bold py-3.5 rounded-xl shadow-lg flex items-center justify-center"
            >
              {loading ? <Loader2 className="animate-spin" /> : "Login"}
            </motion.button>
          )}
        </form>

        <div className="mt-8 text-center text-sm font-semibold text-rose-950">
          Don&apos;t have an Account?
          <Link to="/signup" className="ml-1 border-b-2 border-rose-900 font-bold">
            Register
          </Link>
        </div>
      </motion.div>
    </AuthLayout>
  );
};

export default Login;